import { z } from 'zod';
import { LAYER_LOOK } from './layer-look.js';
import { RARITY_BANDS } from './rarity.js';
/**
 * The unlock moment (`docs/achievements-plan.md` §7.4): one animation and one sound, the same in
 * every BoardFlight game, which BoardFlight's layer plays when the server confirms a new unlock.
 *
 * **The spec, and the only place its numbers live** (§7.4.6). The update card's rule applies:
 * renderers stay in agreement only if none of them owns the number. `patcher/dump-constants.mjs`
 * emits this module and `patcher/generate_config.py` bakes it into the web layer at patch time, as
 * it bakes `layer-look.ts`; the playground's dev sink and the dashboard's *Preview* hand the same
 * object to the same renderer (`unlock-moment-web.ts`). A value typed into a renderer instead is
 * the bug this file exists to prevent.
 *
 * - **Times** are milliseconds from the moment's first frame.
 * - **Lengths** are CSS pixels on a Board's 1920-wide panel, which a Board reports at density 1 —
 *   the plan's "dp", as in `layer-look.ts`.
 * - **Curves** are cubic-bézier control points; the one spring is Material's default spatial
 *   spring, which a renderer samples into the curve its platform takes.
 * - **Colours** are `#RRGGBB` or `#RRGGBBAA`, the update card's tokens where the two share one.
 */
/* ---------------------------------------------------------------------------------- tiers */
/**
 * One family, four sizes (§7.4.3): every tier keeps the whole opening and the whole base sound,
 * and a rarer one adds a layer after impact. Restraint over spectacle, because amplified feedback
 * lowered players' sense of competence (§7.4.1).
 */
export const UNLOCK_TIERS = ['unlocked', 'rare', 'ultra', 'mastered'];
export const UnlockTier = z.enum(UNLOCK_TIERS);
/** Below this share of the `public` channel's players, an unlock is *Rare* (§7.4.3, §9.5). */
export const UNLOCK_RARE_BELOW = 0.15;
/** And below this, *Ultra rare*. */
export const UNLOCK_ULTRA_BELOW = 0.05;
/**
 * Which tier an unlock plays at, from what the server knows when it answers the record (§8.2):
 * the unlock that completes the game is *Mastered*; otherwise its rarity decides, where there is a
 * rarity to read — `null` below twenty players, and then it is plain *Unlocked*, as it always is
 * for an unlock the layer plays from its own cache because the answer was late. A flourish shown
 * on a guess is worse than none.
 */
export function unlockTier(facts) {
    if (facts.mastered)
        return 'mastered';
    const rarity = facts.rarity;
    if (typeof rarity !== 'number' || !Number.isFinite(rarity))
        return 'unlocked';
    if (rarity < UNLOCK_ULTRA_BELOW)
        return 'ultra';
    if (rarity < UNLOCK_RARE_BELOW)
        return 'rare';
    return 'unlocked';
}
/* ------------------------------------------------------------------------------ the sound */
/**
 * The four files the moment plays, one per tier, in `patcher/assets/unlock/` — a base and three
 * layers mixed over it by script, so every file *starts* the same (§7.4.4, §7.4.9).
 *
 * **This is the signature** (owner, 2026-10-20; phase SIG). A1 synthesised these tones as a stand-in
 * (`patcher/assets/unlock/make_sound.py`), and the owner chose them over the sound lab's ElevenLabs
 * takes; `SOURCE.md` beside them is their record. They are frozen: changing a digest here is a
 * design review (§7.4.4).
 */
export const UNLOCK_SOUNDS = {
    placeholder: false,
    /** The container every file is in. A layer decodes whatever this says, once, at the first unlock. */
    contentType: 'audio/mp4',
    files: {
        unlocked: { file: 'unlocked.m4a', sha256: 'f54745192eea57626fe19d3bbe8b0e30effa4aadc7616e0302c215e3e6030c1d' },
        rare: { file: 'rare.m4a', sha256: '5f5b2273e5e50133f449987c6df5efa6fd4ad7f4d57b5406e67c76fd338f08f3' },
        ultra: { file: 'ultra.m4a', sha256: 'abdcf4999d5a981e5e179be3208e3de9cc8382f2527a8bc3ae7795e03c5d545c' },
        mastered: { file: 'mastered.m4a', sha256: 'a02d9823d2983c554652a8e3183ac0f56385e59d18a848469cba86c5c8bf0e65' },
    },
};
/* ------------------------------------------------------------------------------- the spec */
export const UNLOCK_MOMENT = {
    /**
     * The timeline (§7.4.2): the purple tile lifts off the grid, turns over, and lands as the
     * achievement. Every renderer, and the frame test that holds them together, reads these.
     */
    time: {
        /** The mark appears at the icon's place, at `mark.startScale`, fading in. */
        appear: 0,
        appearMs: 100,
        /** Anticipation: the purple tile dips to `mark.dipScale`. */
        dip: 100,
        dipMs: 80,
        /** Lift: up and to the right, tilted, the sparks off its corner; the card starts to unfold. */
        lift: 180,
        liftMs: 60,
        /** The turn: the tile flips and grows into the icon slot, landing on `impact`. */
        turn: 240,
        /** **Impact.** The icon is in place, one ring pulses out, and the sound's hit lands here. */
        impact: 300,
        ringMs: 420,
        /** Reading order, one thing at a time: the eyebrow, the name, the points, the rarity line. */
        text: [360, 420, 480, 540],
        textMs: 160,
        /** The points count up from 0 between these two. */
        countFrom: 480,
        countTo: 620,
        /** Everything is in place. */
        settled: 620,
        /** The exit: the same story backwards, faster than the entrance (§7.4.1). */
        exitMs: 220,
        /** When the exit starts, by tier: the hold column of §7.4.3. */
        exitAt: { unlocked: 4000, rare: 4000, ultra: 5000, mastered: 6000 },
        /** The rarer tiers' layer after impact: the shimmer's one sweep across the icon. */
        shimmer: 380,
        shimmerMs: 600,
        /** *Mastered*: the three ivory tiles slide in and frame the icon. */
        frame: 360,
        frameMs: 320,
        /** Where motion is reduced, the whole card simply fades in over this long. */
        reducedMs: 200,
        /** The exit's first part, as a share of `exitMs`: the card folds back into the slot meanwhile. */
        foldShare: 0.55,
        /** And the words' fade, as a share of the fold: gone before the card folds out from under them. */
        wordsShare: 0.5,
        /** The points count up eased out by this power, so the last few land slowly enough to read. */
        countPower: 3,
    },
    /** The card's spring as it unfolds from the mark (Material's default spatial spring, §7.4.1). */
    spring: {
        damping: 0.9,
        stiffness: 700,
        /** Settled when its envelope falls below this: how long the spring runs. */
        settle: 0.001,
        /** Where a renderer cannot take the formula, it is sampled at this many equal steps and joined. */
        samples: 24,
    },
    /** The fades and slides that are not the spring: the library's `--ease-out`. */
    ease: LAYER_LOOK.motion.ease,
    /** The exit and the dip, which accelerate away rather than settle. */
    easeIn: [0.4, 0, 1, 1],
    /** BoardFlight's mark at the icon's place (`BrandMark`: four tiles on a 148-unit board). */
    mark: {
        size: 64,
        startScale: 0.6,
        dipScale: 0.92,
        /** One tile's side and the gap between tiles, as fractions of the mark: 60 and 14 of 134. */
        tile: 60 / 134,
        gap: 14 / 134,
        /** A tile's corner, as a fraction of its side: 15 of 60, the brand mark's `size / 4`. */
        radius: 0.25,
        liftX: 10,
        liftY: -16,
        /** Degrees. */
        liftTurn: 12,
        /** *Mastered*: how far past the icon's corner each ivory tile reaches when it slides out to frame it. */
        frame: 9,
        /** How far the eye is from the slot as the tile turns over: CSS's `perspective`. */
        perspective: 600,
    },
    /**
     * The sparks off the purple tile's corner at lift: three, or six for *Ultra rare*. They fan out
     * from `fromDeg` across `spreadDeg` (counter-clockwise from the right), every second one reaching
     * `shortReach` as far, and shrink to `endScale` as they fade.
     */
    sparks: { count: 3, countUltra: 6, size: 8, reach: 44, ms: 360, fromDeg: 15, spreadDeg: 90, shortReach: 0.7, endScale: 0.4 },
    /**
     * The rarer tiers' shimmer: a band of light `width` of the icon wide, `overhang` taller than it at
     * each end and leaning at `angle` degrees, swept once from `from` to `to` of its own width.
     */
    shimmer: { width: 0.45, overhang: 0.2, angle: 100, from: -1.3, to: 3.3 },
    /** The card. */
    card: {
        width: 520,
        /** Of the screen, on one narrower than the card. */
        maxWidthPercent: 92,
        top: 24,
        side: 24,
        padY: 18,
        padX: 22,
        radius: 24,
        gap: 18,
        /** The icon, masked to BoardFlight's rounded tile — every surface masks it the same (§5.4). */
        icon: 88,
        iconRadius: 22,
        /** The ring that pulses out from the icon at impact: how far it grows, as a scale, from this opacity. */
        ringScale: 1.55,
        ringWidth: 3,
        ringOpacity: 0.9,
        /** The inset line round the card. */
        hairline: 1,
        /** *Ultra rare*'s gold edge. */
        edgeWidth: 2,
        shadowY: 18,
        shadowBlur: 48,
    },
    type: {
        eyebrow: { size: 17, weight: 400, lineHeight: 1.25 },
        name: { size: 28, weight: 800, tracking: -0.02, lineHeight: 1.15 },
        meta: { size: 19, weight: 400, lineHeight: 1.3 },
        points: { size: 17, weight: 800, height: 28, padX: 11 },
        /** How far each line rises as it fades in. */
        rise: 6,
        /** Between the lines; above the points' row; between the points and the rarity line. */
        lineGap: 2,
        metaTop: 4,
        metaGap: 10,
    },
    color: {
        surface: LAYER_LOOK.color.surface,
        hairline: LAYER_LOOK.color.hairline,
        shadow: LAYER_LOOK.color.shadow,
        text: LAYER_LOOK.color.text,
        textSoft: LAYER_LOOK.color.textSoft,
        brand: LAYER_LOOK.color.brand,
        onBrand: LAYER_LOOK.color.onBrand,
        markTile: LAYER_LOOK.color.markTile,
        markLift: LAYER_LOOK.color.markLift,
        /** The ring at impact: the brand at .55. */
        ring: '#7815FF8C',
        spark: '#B98BFF',
        /** *Ultra rare*'s edge and *Mastered*'s frame. */
        gold: '#E8B64C',
        /** The shimmer's light, at its brightest: ivory at .55. */
        shimmer: '#FFFEF18C',
        /** Where an icon is missing: the slot, in the brand. */
        iconEmpty: '#2A2338',
    },
    /**
     * A locked icon, wherever one is drawn (§5.4): the same file, greyscale at 40% brightness. Not the
     * moment's — nothing locked is ever on its card — but it is the same family's rule, and the
     * dashboard's grid reads it from here.
     */
    locked: { saturate: 0, brightness: 0.4 },
    /**
     * Many at once (§7.4.5): one card at a time, about this far apart; the first plays in full with
     * its sound, the next two are silent and plain, and after three in a row one card says how many
     * more. One sound per burst, the burst's rarest.
     */
    burst: { gapMs: 1200, cards: 3 },
    /** With no answer from the server in this long, the layer plays the plain tier from its own cache. */
    fallbackMs: 3000,
    /** A hold lets go by itself after this long, so a game cannot swallow its unlocks (§7.4.5). */
    holdMaxMs: 30_000,
    /**
     * The sound's timing (§7.4.4): where the hit falls in each file, so the layer starts it that much
     * before impact, and the window inside which nobody notices sound and picture apart (ITU-R
     * BT.1359, §7.4.1). Later than `lateMs` past impact the card plays silently; it is never delayed
     * waiting for sound.
     */
    sound: { hitMs: 280, earlyMs: 45, lateMs: 125 },
    /**
     * What the card says. Every surface shows the same words, and the layer carries them as `\u`
     * escapes (the generator writes them with `ensure_ascii`).
     */
    words: {
        eyebrow: 'Achievement unlocked',
        mastered: 'Game mastered',
        guest: 'Guest: not kept',
        /** `{n}` is the count. */
        more: '+{n} more achievements',
        moreOne: '+1 more achievement',
        /** The rarity line: `{word}` is the band, `{percent}` the share as `rarityPercent` writes it. */
        rarity: '{word} \u00b7 {percent} of players',
        /** For a share in the commonest band, which is no boast: the share alone. */
        common: '{percent} of players',
    },
    /**
     * The bands the rarity line names (`rarity.ts`), carried so a renderer that imports nothing
     * writes the same word: whole percents, *< 1%* below one.
     */
    rarity: { bands: RARITY_BANDS },
};
//# sourceMappingURL=unlock-moment.js.map